import type { Action, Agent, FieldConfig, Hook, Model } from '@lucidtech/las-sdk-browser';
import { useCallback } from 'react';

import { CREATE_PREDICTION_FUNCTION_ID, HOOK_EVALUATE_DOCUMENT } from '@/constants';
import { toAgentId, useCreateAction, useCreateHook, useCreateModel, useUpdateAgent } from '@/hooks/api';

export type UseAddModelOnSuccess = ({
  model,
  action,
  hook,
  agent,
}: {
  model: Model;
  action: Action;
  hook: Hook;
  agent: Agent;
}) => void;

export type UseAddModelOnError = ({ error }: { error: Error }) => void;

export type UseAddModelOpts = {
  onSuccess?: UseAddModelOnSuccess;
  onError?: UseAddModelOnError;
};

export const useAddModel = (opts?: UseAddModelOpts) => {
  const { onSuccess: onSuccessFnBase, onError: onErrorFnBase } = opts ?? {};

  const {
    mutateAsync: updateAgent,
    isPending: isPendingUpdateAgent,
    isSuccess: isSuccessUpdateAgent,
    error: errorUpdateAgent,
  } = useUpdateAgent();

  const {
    mutateAsync: createAction,
    isPending: isPendingCreateAction,
    isSuccess: isSuccessCreateAction,
    error: errorCreateAction,
  } = useCreateAction();

  const {
    mutateAsync: createModel,
    isPending: isPendingCreateModel,
    isSuccess: isSuccessCreateModel,
    error: errorCreateModel,
  } = useCreateModel();

  const {
    mutateAsync: createHook,
    isPending: isPendingCreateHook,
    isSuccess: isSuccessCreateHook,
    error: errorCreateHook,
  } = useCreateHook();

  const addModel = useCallback(
    async ({
      agentId,
      name,
      fieldConfig,
      onSuccess: onSuccessFn,
      onError: onErrorFn,
    }: {
      agentId: string;
      name?: string;
      fieldConfig: FieldConfig;
      onSuccess?: UseAddModelOnSuccess;
      onError?: UseAddModelOnError;
    }) => {
      const onSuccess: UseAddModelOnSuccess = (...args) => {
        onSuccessFnBase?.(...args);
        onSuccessFn?.(...args);
      };
      const onError: UseAddModelOnError = (...args) => {
        onErrorFnBase?.(...args);
        onErrorFn?.(...args);
      };

      try {
        const model = await createModel({
          fieldConfig: fieldConfig,
          name: name,
        });

        const action = await createAction({
          config: { modelId: model.modelId },
          functionId: CREATE_PREDICTION_FUNCTION_ID,
        });

        const hook = await createHook({
          functionId: HOOK_EVALUATE_DOCUMENT,
          config: {},
          trigger: 'Document is Created',
          trueActionId: action.actionId,
        });

        const agent = await updateAgent({
          id: toAgentId(agentId),
          updates: (agent: Agent) => ({
            resourceIds: [...agent.resourceIds, model.modelId, action.actionId, hook.hookId],
          }),
        });

        onSuccess?.({ model, action, hook, agent });

        return model;
      } catch (error) {
        if (error instanceof Error) {
          onError?.({ error });
        }
      }
    },
    [createAction, createHook, createModel, onErrorFnBase, onSuccessFnBase, updateAgent]
  );

  return {
    addModel,
    isPending: isPendingCreateModel || isPendingCreateAction || isPendingCreateHook || isPendingUpdateAgent,
    isSuccess: isSuccessCreateModel && isSuccessCreateAction && isSuccessCreateHook && isSuccessUpdateAgent,
    errors: [errorCreateModel, errorCreateAction, errorCreateHook, errorUpdateAgent].filter((e) => !!e),
  };
};
